import { Check, X } from 'lucide-react';
import type { JobStatus } from '@conversia/shared-types';
import { timelineFromEstado, type TimelineStepState } from '@/lib/job-timeline';
import { cn } from '@/lib/cn';

/**
 * <JobTimeline> — progreso visual tipo Uber del trabajo: "Técnico en camino →
 * Técnico ha llegado → Trabajo realizado" con el paso actual resaltado.
 * Presentacional puro; el mapeo estado→pasos vive en lib/job-timeline (testeado).
 */

const DOT_STYLE: Record<TimelineStepState, string> = {
  done: 'bg-emerald-500 text-white',
  current: 'bg-blue-500 text-white ring-4 ring-blue-500/25 animate-pulse',
  pending: 'bg-slate-200 text-slate-400 dark:bg-slate-700 dark:text-slate-500',
  failed: 'bg-red-500 text-white',
};

const LABEL_STYLE: Record<TimelineStepState, string> = {
  done: 'text-emerald-600 dark:text-emerald-400',
  current: 'font-semibold text-blue-600 dark:text-blue-400',
  pending: 'text-slate-400',
  failed: 'font-semibold text-red-600 dark:text-red-400',
};

export function JobTimeline({ estado, className }: { estado: JobStatus; className?: string }) {
  const steps = timelineFromEstado(estado);
  return (
    <ol className={cn('flex items-start', className)} aria-label="Progreso del trabajo">
      {steps.map((step, i) => (
        <li key={step.label} className={cn('flex items-start', i > 0 && 'flex-1')}>
          {i > 0 && (
            <span
              aria-hidden
              className={cn(
                'mt-2.5 h-0.5 min-w-4 flex-1',
                step.state === 'pending'
                  ? 'bg-slate-200 dark:bg-slate-700'
                  : step.state === 'failed'
                    ? 'bg-red-300 dark:bg-red-800'
                    : 'bg-emerald-300 dark:bg-emerald-800',
              )}
            />
          )}
          <span className="flex max-w-24 flex-col items-center gap-1 px-1 text-center">
            <span
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px]',
                DOT_STYLE[step.state],
              )}
            >
              {step.state === 'done' && <Check className="h-3 w-3" />}
              {step.state === 'failed' && <X className="h-3 w-3" />}
            </span>
            <span className={cn('text-[10px] leading-tight', LABEL_STYLE[step.state])}>
              {step.label}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}
